<template>
  <div class="xl:w-full xl:h-full flex xl:flex-nowrap flex-wrap">
    <div
      class="xl:w-2/3 sm:items-start items-center sm:text-left text-center w-full xl:h-full xl:pt-14 pt-6 xl:px-14 lg:px-20 sm:px-14 px-5 text-gray-700 text-base"
    >
      <h1 class="mt-10">Client API</h1>

      <h2 class="mt-10" id="introduction">Introduction</h2>
      <p>
        This document describes how you can make a request to our endpoint to
        get back enriched data for your receipt.
      </p>
      <p>
        The flow to make a request and a response is as follows: you make a POST
        request to our endpoint and at a later time we will send a POST request
        back to your specified address.
      </p>
      <p>
        Due to the nature of our data, we send back the the data as a webhook
        event. Some merchants send us the information as the transaction is
        happening, others send it in batches once a day. This means that if we
        don't find the data for your receipt immediately, we will keep your
        request on our side until the data appears.
      </p>
      <p>
        This also means that you have to sends us a unique ID in each request so
        you can track if you got back the response (we will include this ID in
        the payload sent back to you).
      </p>

      <h3 id="modes">Modes</h3>
      <table class="table-fixed bg-gray-100 rounded-md mb-10">
        <thead>
          <tr>
            <th class="w-1/5 px-4 py-2">Mode</th>
            <th class="w-1/5 px-4 py-2">Endpoint</th>
            <th class="w-3/5 px-4 py-2">Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="font-bold text-sm px-4 py-2">Development</td>
            <td class="text-blue-400 px-4 py-2"><a target="_blank" href="https://output.api.development.getbanyan.com/receipt">https://output.api.development.getbanyan.com/receipt</a></td>
            <td class="text-sm px-4 py-2">
              Use the Development API to send test data to Banyan with your API keys. Banyan will send back sample data to the development endpoint you will have provided to your Banyan representative.
            </td>
          </tr>
          <tr>
            <td class="font-bold text-sm px-4 py-2">
              Production
            </td>
            <td class="text-blue-400 px-4 py-2"><a target="_blank" href="https://output.api.getbanyan.com/receipt">https://output.api.getbanyan.com/receipt</a></td>
            <td class="text-sm px-4 py-2">
              Use the Production API to send live data to Banyan with your API keys. Banyan will send back live data to the production endpoint you will have provided to your Banyan representative.
            </td>
          </tr>
        </tbody>
      </table>
      <h3 id="webhook">Webhook</h3>
      <p>
        To receive webhook events about the result of your query, you have to
        get in touch with us and provide the following:
      </p>
      <ul class="list-disc text-left overflow-auto pl-10 mb-4">
        <li>Your URL endpoint.</li>
        <li>The API Key that you want us to use when sending back data.</li>
      </ul>
      <p>
        We are going to pass the API Key in the header under the value of
        <code>x-api-key</code> when sending back data.
      </p>
      <h3 id="endpoint-url">Endpoint URL</h3>
      <ul class="list-disc text-left overflow-auto pl-10 mb-4">
        <li>
          Production: <code>https://output.api.getbanyan.com/receipt</code>
        </li>
        <li>
          Development:
          <code>https://output.api.development.getbanyan.com/receipt</code>
        </li>
      </ul>
      <h2 class="mt-10" id="example-payloads">Example Payloads</h2>
      <h3 class="ml-5" id="example1-internal">Example 1 - Internal</h3>
      <v-card dark class="text-left p-4" >
        <v-tabs v-model="tab3" dark>
          <v-tab v-for="item in chart3" :key="item.tab">
            {{ item.tab }}
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab3" dark>
          <v-tab-item v-for="item in chart3" :key="item.tab">
            <v-card flat>
              <div v-if="item.tab == 'Example'">
                <v-card-text
                  ><div>
                    <pre>{{ item.content }}</pre>
                  </div></v-card-text
                >
              </div>
              <div v-else>
                <v-data-table :headers="headers" :items="item.content" dense>
                </v-data-table>
              </div>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-card>

      <h3 class="mt-10" id="example2-address-conversion">
        Example 2 - Address conversion
      </h3>
      <v-card dark class="text-left p-4">
        <v-tabs v-model="tab4" dark>
          <v-tab v-for="item in chart4" :key="item.tab">
            {{ item.tab }}
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab4" dark>
          <v-tab-item v-for="item in chart4" :key="item.tab">
            <v-card flat>
              <div v-if="item.tab == 'Example'">
                <v-card-text
                  ><div>
                    <pre>{{ item.content }}</pre>
                  </div></v-card-text
                >
              </div>
              <div v-else>
                <v-data-table :headers="headers" :items="item.content">
                </v-data-table>
              </div>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-card>

      <h2 class="mt-10" id="example-response">Example Response</h2>
      <v-card dark class="text-left p-4">
        <v-tabs v-model="tab5" dark>
          <v-tab v-for="item in chart5" :key="item.tab">
            {{ item.tab }}
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab5" dark>
          <v-tab-item v-for="item in chart5" :key="item.tab">
            <v-card flat>
              <div v-if="item.tab == 'Example'">
                <v-card-text
                  ><div>
                    <pre>{{ item.content }}</pre>
                  </div></v-card-text
                >
              </div>
              <div v-else>
                <v-data-table :headers="headers" :items="item.content">
                </v-data-table>
              </div>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-card>

      <h2 class="mt-10" id="error-handling">Error Handling</h2>
      <p>
        If the receipt is not in our database and we cannot find it externally,
        we will send a message saying so.
      </p>
      <v-card dark class="text-left p-4">
        <v-tabs v-model="tab6" dark>
          <v-tab v-for="item in chart6" :key="item.tab">
            {{ item.tab }}
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab6" dark>
          <v-tab-item v-for="item in chart6" :key="item.tab">
            <v-card flat>
              <div v-if="item.tab == 'Example'">
                <v-card-text
                  ><div>
                    <pre>{{ item.content }}</pre>
                  </div></v-card-text
                >
              </div>
              <div v-else>
                <v-data-table :headers="headers" :items="item.content">
                </v-data-table>
              </div>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-card>

      <h2 class="mt-10" id="retry-mechanism">Retry Mechanism</h2>
      <p>
        If we are unable to send data to the webhook endpoint for any reason, we
        will continue to try and hit the endpoint every minute for fifteen
        minutes before dropping the message.
      </p>
    </div>
    <div
      class="xl:w-1/3 xl:block hidden w-full xl:self-stretch px-6 relative"
    >
      <div class="overflow-y-auto fixed h-5/6 mb-30" :class="[isOffset ? 'top-4': 'top-20']">
        <p class="ml-10">
          <vue-fontawesome
            icon="clock-o"
            class="pr-2"
            size="1"
            color="#FCD34D"
          ></vue-fontawesome>
          <span class="text-xs">Updated on january 27, 2017</span>
        </p>
        <ul class="ml-8">
          <li class="text-base py-2 hover:text-blue-500 hover:bg-gray-50 px-2">
            <span @click="scrollToElement('#introduction')">Introduction</span>
          </li>
          <li class="text-base py-2 hover:text-blue-500 hover:bg-gray-50 px-2">
            <span @click="scrollToElement('#modes')">Modes</span>
          </li>
          <li class="text-base py-2 hover:text-blue-500 hover:bg-gray-50 px-2">
            <span @click="scrollToElement('#webhook')">Webhook</span>
          </li>
          <li class="text-base py-2 hover:text-blue-500 hover:bg-gray-50 px-2">
            <span @click="scrollToElement('#endpoint-url')">Endpoint URL</span>
          </li>
          <li class="text-base py-2 hover:text-blue-500 hover:bg-gray-50 px-2">
            <span @click="scrollToElement('#example-payloads')">Example Payloads</span>
          </li>
          <li class="text-base py-2 hover:text-blue-500 hover:bg-gray-50 px-2">
            <span @click="scrollToElement('#example1-internal')"
              >Example 1 - Internal</span
            >
          </li>
          <li class="text-base py-2 hover:text-blue-500 hover:bg-gray-50 px-2">
            <span @click="scrollToElement('#example2-address-conversion')"
              >Example 2 - Address conversion</span
            >
          </li>
          <li class="text-base py-2 hover:text-blue-500 hover:bg-gray-50 px-2">
            <span @click="scrollToElement('#example-response')">Example Response</span>
          </li>
          <li class="text-base py-2 hover:text-blue-500 hover:bg-gray-50 px-2">
            <span @click="scrollToElement('#error-handling')">Error Handling</span>
          </li>
          <li class="text-base py-2 hover:text-blue-500 hover:bg-gray-50 px-2">
            <span @click="scrollToElement('#retry-mechanism')">Retry Mechanism</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
export default {
  name: "Client_API",
  computed: {
    ...mapGetters("common", ["isOffset"]),
  },
  data: () => ({
    headers: [
      {
        text: "Name",
        align: "start",
        sortable: false,
        value: "name",
      },
      {
        text: "Type",
        value: "type",
      },
    ],
    tab3: null,
    chart3: [
      {
        tab: "Example",
        content: `{
        "customerToken": "007",
        "uniqueIdentifier": "0x00000001",
        "receipt": {
          "storeId": "1234",
          "merchantName": "walmart",
          "datetime": "2020-12-30T00:00:00.000Z",
          "cardType": "visa",
          "last4": "1234",
          "totalAmount": 12.24
        }
}`,
      },
      {
        tab: "Structure",
        content: [
          {
            name: "customerToken",
            type: "string",
          },
          {
            name: "uniqueIdentifier",
            type: "string",
          },
          {
            name: "storeId",
            type: "string",
          },
          {
            name: "merchantName",
            type: "string",
          },
          {
            name: "datetime",
            type: "string",
          },
          {
            name: "cardType",
            type: "string",
          },
          {
            name: "last4",
            type: "string",
          },
          {
            name: "totalAmount",
            type: "number",
          },
        ],
      },
    ],

    tab4: null,
    chart4: [
      {
        tab: "Example",
        content: `{
    "customerToken": "007",
    "uniqueIdentifier": "0x00000002",
    "receipt": {
        "merchantName": "walmart",
        "datetime": "2020-05-08T15:30:37.000Z",
        "cardType": "debit",
        "last4": "9631",
        "totalAmount": 6.76,
        "address": {
            "country": "us",
            "state": "fl",
            "city": "sebring",
            "postalCode": "33870",
            "addressLineOne": "3525 US HIGHWAY 27 N",
            "addressLineTwo": ""
        }
    }
}`,
      },
      {
        tab: "Structure",
        content: [
          {
            name: "customerToken",
            type: "string",
          },
          {
            name: "uniqueIdentifier",
            type: "string",
          },
          {
            name: "merchantName",
            type: "string",
          },
          {
            name: "datetime",
            type: "string",
          },
          {
            name: "cardType",
            type: "string",
          },
          {
            name: "last4",
            type: "string",
          },
          {
            name: "totalAmount",
            type: "number",
          },
          {
            name: "country",
            type: "string",
          },
          {
            name: "state",
            type: "string",
          },
          {
            name: "city",
            type: "string",
          },
          {
            name: "postalCode",
            type: "string",
          },
          {
            name: "addressLineOne",
            type: "string",
          },
          {
            name: "addressLineTwo",
            type: "string",
          },
        ],
      },
    ],

    tab5: null,
    chart5: [
      {
        tab: "Example",
        content: `{
    "customerToken": "007",
    "uniqueIdentifier": "0x00000002",
    "receipt": {
        "store": {
            "displayName": "Neptune Supercenter",
            "phoneNumber": "732-922-8084",
            "address": {
                "addressLineOne": "3575 STATE ROUTE 66",
                "addressLineTwo": "",
                "country": "us",
                "state": "cs",
                "city": "neptune",
                "postalCode": "07753"
            }
        },
        "transaction": {
            "transactionID": "27804549671483058136",
            "datetime": "2020-05-08T15:30:37.000Z",
            "cardType": "debit",
            "last4": "0000",
            "subtotal": 6.76,
            "taxAmount": 0,
            "changeDue": 0,
            "items": [
                {
                    "itemId": "0576407142",
                    "quantity": 1,
                    "price": 3.97,
                    "description": "Equate Moisturizing Hand Sanitizer with Vitamin E, 34 fl oz - May Ship with a Flip Top Cap",
                    "upc": "068113124766"
                },
                {
                    "itemId": "0578188885",
                    "quantity": 1,
                    "price": 3.38,
                    "description": "LaCroix LimonCello Sparkling Water - 8pk/12 fl oz Cans, 8 / Pack (Quantity)",
                    "upc": "001299322111"
                }
            ]
        }
    }
}`,
      },
      {
        tab: "Structure",
        content: [
          {
            name: "customerToken",
            type: "string",
          },
          {
            name: "uniqueIdentifier",
            type: "string",
          },
          {
            name: "displayName",
            type: "string",
          },
          {
            name: "phoneNumber",
            type: "string",
          },
          {
            name: "addressLineOne",
            type: "string",
          },
          {
            name: "addressLineTwo",
            type: "string",
          },
          {
            name: "country",
            type: "string",
          },
          {
            name: "state",
            type: "string",
          },
          {
            name: "city",
            type: "string",
          },
          {
            name: "postalCode",
            type: "string",
          },
          {
            name: "transactionID",
            type: "string",
          },
          {
            name: "datetime",
            type: "string",
          },
          {
            name: "cardType",
            type: "string",
          },
          {
            name: "last4",
            type: "string",
          },
          {
            name: "subtotal",
            type: "number",
          },
          {
            name: "taxAmount",
            type: "number",
          },
          {
            name: "changeDue",
            type: "number",
          },
          {
            name: "itemId",
            type: "string",
          },
          {
            name: "quantity",
            type: "number",
          },
          {
            name: "price",
            type: "number",
          },
          {
            name: "description",
            type: "string",
          },
          {
            name: "upc",
            type: "string",
          },
          {
            name: "itemId",
            type: "string",
          },
          {
            name: "quantity",
            type: "number",
          },
          {
            name: "price",
            type: "number",
          },
          {
            name: "description",
            type: "string",
          },
          {
            name: "upc",
            type: "string",
          },
        ],
      },
    ],

    tab6: null,
    chart6: [
      {
        tab: "Example",
        content: `{
    "customerToken": "0x07",
    "uniqueIdentifier": "0x00000002",
    "error": {
        "message": "Receipt not found",
        "code": 404
    }
}`,
      },
      {
        tab: "Structure",
        content: [
          {
            name: "customerToken",
            type: "string",
          },
          {
            name: "uniqueIdentifier",
            type: "string",
          },
          {
            name: "message",
            type: "string",
          },
          {
            name: "code",
            type: "number",
          },
        ],
      },
    ],
    // DATA END
  }),
  methods: {
    scrollToElement(el) {
      document.querySelector(el).scrollIntoView({
        behavior: 'smooth'
      });
    }
  }
};
</script>
<style scoped>
.v-tabs-items {
  overflow-x: auto;
}
</style>